import { ref } from 'vue'
import axios from '@/utils/axios'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'

export default function useContentPackUsers() {
  const RESOURCE = '/content-pack-user'
  const { showSnackbar } = useSnackbar()
  const isLoading = ref(false)

  const fetchContentPacksByUserId = async userId => {
    isLoading.value = true
    try {
      const response = await axios.get(`${RESOURCE}/user/${userId}/content-packs`, {
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      return response.data || []
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null
      showSnackbar(
        `Failed to fetch content packs for user:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const fetchUsersByContentPackId = async contentPackId => {
    isLoading.value = true
    try {
      const response = await axios.get(`${RESOURCE}/content-pack/${contentPackId}/users`, {
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      return response.data || []
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null
      showSnackbar(
        `Failed to fetch users for content pack:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const upsertContentPackUser = async (userId, contentPackId, options = {}) => {
    isLoading.value = true
    try {
      await axios.post(RESOURCE, {
        userId,
        contentPackId,
        purchased: options.purchased,
      }, {
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      if (!options.silent) {
        showSnackbar('User content packs updated successfully', SnackbarType.SUCCESS)
      }
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null
      showSnackbar(
        `Failed to update user content packs:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const deleteContentPackUser = async (userId, contentPackId, options = {}) => {
    isLoading.value = true
    try {
      await axios.delete(RESOURCE, {
        data: {
          userId,
          contentPackId,
        },
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      if (!options.silent) {
        showSnackbar('User content packs updated successfully', SnackbarType.SUCCESS)
      }
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null
      showSnackbar(
        `Failed to update user content packs:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )
      throw error
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading,
    fetchContentPacksByUserId,
    fetchUsersByContentPackId,
    upsertContentPackUser,
    deleteContentPackUser,
  }
}
